/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    List,
    ListItem,
    Heading,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {generateStyles} from '../optimizely/utils/utils'
import { FormattedMessage } from 'react-intl'

const LinksList = ({
    links = [],
    heading = '',
    variant,
    color,
    onLinkClick,
    headingLinkRef,
    ...otherProps
}) => {
    const styles = useMultiStyleConfig('LinksList', {variant})

    return (
        <Box {...styles.container} {...(color ? {color: color} : {})} {...otherProps}>

            {links &&
                (variant === 'horizontal' ? (
                    <List {...styles.list} data-testid="horizontal-list">
                        {heading && (
                            <ListItem>
                                {heading.href ? (
                                    <Box {...heading.wrapperStyles}>
                                        <Box {...styles.wrapperStyles}>
                                            <Link
                                                to={heading.href}
                                                onClick={onLinkClick}
                                                ref={headingLinkRef}
                                                {...styles.headingLink}
                                                {...styles.headingAll}
                                                color={heading?.cmsCategoryReplacement?.TextColor}
                                                backgroundColor={heading?.cmsCategoryReplacement?.TextBackgroundColor}
                                                {...(heading?.cmsCategoryReplacement?.TextBackgroundColor && styles.bgColorPadding)}
                                            >
                                                {heading?.cmsCategoryReplacement?.OverrideName ? (
                                                    'All ' + heading?.cmsCategoryReplacement?.OverrideName
                                                ) : (
                                                    heading.text
                                                )}
                                            </Link>
                                            {heading?.cmsCategoryReplacement?.Badge && (
                                                <Box {...(generateStyles(heading?.cmsCategoryReplacement?.BadgeStyle, { ...styles.badge}))}>
                                                    {heading?.cmsCategoryReplacement?.Badge}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box {...heading.wrapperStyles}>
                                        <Box {...styles.wrapperStyles}>
                                            <Text {...styles.headingAll}
                                                {...(heading?.cmsCategoryReplacement?.TextBackgroundColor && styles.bgColorPadding)}>
                                                {heading?.cmsCategoryReplacement?.OverrideName ? (
                                                    'All ' + heading?.cmsCategoryReplacement?.OverrideName
                                                ) : (
                                                    heading?.text || heading
                                                )}
                                            </Text>
                                            {heading?.cmsCategoryReplacement?.Badge && (
                                                <Box {...(generateStyles(heading?.cmsCategoryReplacement?.BadgeStyle, { ...styles.badge}))}>
                                                    {heading?.cmsCategoryReplacement?.Badge}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </ListItem>
                        )}
                        {links.map((link, i) => (
                            <ListItem key={i} {...styles.listItem} sx={styles.listItemSx}>
                                {link.href ? (
                                    <Link
                                        to={link.href}
                                        onClick={onLinkClick}
                                        {...(link.styles ? link.styles : {})}
                                    >
                                        {link.text}
                                    </Link>
                                ) : (
                                    <Box {...(link.styles ? link.styles : {})}>
                                        {link.text}
                                    </Box>
                                )}
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <List {...styles.list}>
                        {heading && (
                            <ListItem>
                                {heading.href ? (
                                    <Box {...heading.wrapperStyles}>
                                        <Box {...styles.wrapperStyles}>
                                            <Link
                                                to={heading.href}
                                                onClick={onLinkClick}
                                                ref={headingLinkRef}
                                                {...styles.headingLink}
                                                {...styles.headingAll}
                                                {...styles.link}
                                                color={heading?.cmsCategoryReplacement?.TextColor}
                                                backgroundColor={heading?.cmsCategoryReplacement?.TextBackgroundColor}
                                                {...(heading?.cmsCategoryReplacement?.TextBackgroundColor && styles.bgColorPadding)}
                                            >
                                                {heading?.cmsCategoryReplacement?.OverrideName ? (
                                                    'All ' + heading?.cmsCategoryReplacement?.OverrideName
                                                ) : (
                                                    heading.text
                                                )}
                                            </Link>
                                            {heading?.cmsCategoryReplacement?.Badge && (
                                                <Box {...(generateStyles(heading?.cmsCategoryReplacement?.BadgeStyle, { ...styles.badge}))}>
                                                    {heading?.cmsCategoryReplacement?.Badge}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box {...heading.wrapperStyles}>
                                        <Box {...styles.wrapperStyles}>
                                            <Text {...styles.headingAll}
                                                {...(heading?.cmsCategoryReplacement?.TextBackgroundColor && styles.bgColorPadding)}>
                                                {heading?.cmsCategoryReplacement?.OverrideName ? (
                                                    'All ' + heading?.cmsCategoryReplacement?.OverrideName
                                                ) : (
                                                    heading?.text || heading
                                                )}
                                            </Text>
                                            {heading?.cmsCategoryReplacement?.Badge && (
                                                <Box {...(generateStyles(heading?.cmsCategoryReplacement?.BadgeStyle, { ...styles.badge}))}>
                                                    {heading?.cmsCategoryReplacement?.Badge}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </ListItem>
                        )}
                        {links.map((link, i) => (
                            <ListItem key={i} {...link.defaultListStyle}>
                                <Box
                                    {...styles.defaultListStyle}
                                    flexFlow={link?.c_showSeparator ? 'column' : undefined}
                                >
                                    {link.href ? (
                                        <>
                                            <Link
                                                to={link.href}
                                                onClick={onLinkClick}
                                                {...styles.link}
                                                {...(link.styles ? link.styles : {})}
                                                color={link?.cmsCategoryReplacement?.TextColor}
                                                backgroundColor={link?.cmsCategoryReplacement?.TextBackgroundColor}
                                                {...(link?.cmsCategoryReplacement?.TextBackgroundColor && styles.bgColorPadding)}
                                            >
                                                {link?.cmsCategoryReplacement?.OverrideName || link.text}
                                            </Link>
                                            {/* separator */}
                                            {link?.c_showSeparator && (
                                                <Box mt={{base: 0, lg: 3}}>
                                                    <Text>
                                                        <FormattedMessage defaultMessage="-" id="nav.separator" />
                                                    </Text>
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        <Box
                                            {...(link.styles ? link.styles : {})}
                                            color={link?.cmsCategoryReplacement?.TextColor}
                                            backgroundColor={link?.cmsCategoryReplacement?.TextBackgroundColor}
                                            {...(link?.cmsCategoryReplacement?.TextBackgroundColor && styles.bgColorPadding)}
                                        >
                                            {link?.cmsCategoryReplacement?.OverrideName || link.text}
                                        </Box>
                                    )}
                                    {link?.cmsCategoryReplacement?.Badge && (
                                        <Box {...(generateStyles(link?.cmsCategoryReplacement?.BadgeStyle, { ...styles.badge}))}>
                                            {link?.cmsCategoryReplacement?.Badge}
                                        </Box>
                                    )}
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                ))}
        </Box>
    )
}

LinksList.propTypes = {
    links: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string,
            text: PropTypes.string
        })
    ).isRequired,
    heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    variant: PropTypes.oneOf(['vertical', 'horizontal', 'menuItem']),
    color: PropTypes.string,
    onLinkClick: PropTypes.func,
    headingLinkRef: PropTypes.object
}

export default LinksList
